import React, {createContext, useCallback, useContext, useMemo, useState} from 'react'
import {VoteState} from '@colonist/game-state-types'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {useUIGameMyColorContext} from '../../contexts/UIGameMyColorContext'

interface UIGameVoteContextProps {
    onAccept: () => void
    onReject: () => void
    voteState: VoteState | null
    isMyVotePending: boolean
}

const UIGameVoteContext = createContext<UIGameVoteContextProps | undefined>(undefined)

export const useUIGameVoteContext = () => {
    const context = useContext(UIGameVoteContext)
    if(context === undefined) {
        throw new Error('useUIGameVoteContext must be used within a UIGameVoteProvider')
    }
    return context
}

let globalVoteStateSetter: undefined | ((voteState: VoteState | null) => void)
export function setGlobalVoteState(voteState: VoteState | null) {
    if(globalVoteStateSetter) globalVoteStateSetter(voteState)
}

interface UIGameVoteProviderProps {
    children: React.ReactNode
}

export const UIGameVoteProvider = ({children}: UIGameVoteProviderProps) => {
    const [voteState, setVoteState] = useState<VoteState | null>(null)
    const {sendVote} = useUIGameSocketContext()
    const {myColor} = useUIGameMyColorContext()

    const onReceiveVoteState = (newVoteState: VoteState) => {
        if(newVoteState.done) {
            setVoteState(null)
        } else {
            setVoteState(newVoteState)
        }
    }

    globalVoteStateSetter = onReceiveVoteState

    const castVote = useCallback((accepted: boolean) => {
        if(voteState == null) return
        if(accepted) {
            setVoteState({
                ...voteState,
                acceptedVotes: [...voteState.acceptedVotes, myColor],
            })
        } else {
            setVoteState({
                ...voteState,
                declinedVotes: [...voteState.declinedVotes, myColor],
            })
        }
        sendVote(voteState.id, accepted)
    }, [voteState, myColor, sendVote])

    const isMyVotePending = useMemo(() => {
        if(voteState == null) return false

        const allVotes = [...voteState.acceptedVotes, ...voteState.declinedVotes]
        return !allVotes.includes(myColor)
    }, [voteState, myColor])

    const onAccept = useCallback(() => castVote(true), [castVote])
    const onReject = useCallback(() => castVote(false), [castVote])

    const contextValue = useMemo(() => ({
        onAccept: onAccept,
        onReject: onReject,
        voteState: voteState,
        isMyVotePending: isMyVotePending,
    }), [onAccept, onReject, voteState, isMyVotePending])

    return (
        <UIGameVoteContext.Provider value={contextValue}>
            {children}
        </UIGameVoteContext.Provider>
    )
}
